/**
 * /guides/wedding-planning-checklist
 *
 * "Wedding Planning Checklist: What to Organize and When" — the ninth guide,
 * and the first written to be a hub rather than a long-tail answer. Every guide
 * before it answers one question inside one phase of planning; this one is the
 * map those phases hang off, and it is where the rest of the cluster is reached
 * from.
 *
 * ── The differentiator, which is the whole piece ──────────────────────────
 * The search landscape is saturated with hundred-item month-by-month
 * checklists, and a ninth one would be worth nothing to a reader or to a model
 * quoting it. The argument here is DEPENDENCY, not calendar:
 *
 *   constraints (budget, guest count, date, location, venue)
 *     -> what those constraints allow (vendors, catering, design)
 *       -> what confirmed guests allow (headcount, quantities, seating)
 *         -> operations (confirmations, payments, timeline, handoff)
 *
 * Two sections carry it explicitly — "Why the Guest List Comes Before the
 * Seating Plan" and "A Better Way to Think About the Checklist" — and every
 * phase section is written so its tasks are visibly downstream of the phase
 * before. An edit that turns a phase into a flat list of chores removes the
 * only reason this article exists.
 *
 * The months are scaffolding, never a rule. Each window is hedged in the copy
 * itself ("roughly", "often", "depending on availability"), the at-a-glance
 * section says outright that twelve months is a frame rather than a law, and a
 * whole section is given to engagements shorter than a year — which is real
 * search intent, and where a rigid calendar does the most damage.
 *
 * ── Length discipline ─────────────────────────────────────────────────────
 * Commissioned at roughly 2,000-2,400 body words, which for an article with
 * fifteen sections means every list is a sample rather than an inventory. The
 * first draft ran to 2,880 words almost entirely in list items, and was cut
 * back by shortening lists, not by removing sections. Anything added here
 * should replace something rather than extend a list: the value of this guide
 * is the order of the work, and a hundred-item checklist buries it.
 *
 * ── The hedges, which are load-bearing ────────────────────────────────────
 * Nothing here may become an industry rule stated as fact:
 *
 *   - No absolute booking windows. Lead times move with the market, the season
 *     and the vendor, so the article gives the ORDER (hardest to replace
 *     first) and tells the reader to ask each vendor how far ahead they are
 *     taking bookings.
 *   - No budget percentages, and no formula producing a guest count.
 *   - No legal or licensing instruction. Marriage documents, notice periods
 *     and where they are filed vary by jurisdiction and sometimes by venue;
 *     the guide says exactly that and sends the reader to check locally. U.S.
 *     conventions are never presented as universal.
 *   - No prescriptive claims about tips, which vary by country and contract.
 *   - No payment or retention deadlines of our own invention: those belong to
 *     the reader's contracts and suppliers, and the copy says so.
 *
 * Banned by the brief and absent from the copy: "stress-free", "perfect
 * wedding", "guaranteed", "never miss a thing", "ultimate", "everything you
 * need", and any promise that a timeline produces an outcome.
 *
 * ── Boundaries with the guides it links to ────────────────────────────────
 * This article hands off four times and re-explains none of them:
 *
 *   - `how-to-organize-wedding-guest-list` owns building the list.
 *   - `when-should-wedding-guests-rsvp` owns invitation and deadline timing.
 *     The section here is deliberately short: it states the chain (send date
 *     -> response window -> deadline -> final count), names what moves it, and
 *     stops. No numbers, because that guide's numbers are the point of it.
 *   - `how-to-organize-wedding-rsvps` owns tracking replies and follow-ups.
 *   - `wedding-seating-plan` owns the tables.
 *
 * Five contextual links in the body — the four guides above, each at the point
 * the subject genuinely changes, plus /wedding-planning in the closing section.
 * `related.guides` names the two ends of the dependency chain (guest list and
 * seating), which is the relationship this hub actually expresses; the RSVP
 * guides are linked once each in the prose where they are needed rather than a
 * second time in a block, so no href is linked more than twice.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * All in the closing section, all true of the shipped product and already
 * published on /wedding-planning:
 *
 *   - "Agapaé's Planning Hub and Éros are part of the Heirloom plan."
 *     True at `api/constants/planFeatures.mjs`: both hang off
 *     `invitationProduct`, which is `pro` (Heirloom) only.
 *   - planning is divided into categories, each holding its own vendors,
 *     tasks, costs, documents and open decisions
 *   - dated things appear on a calendar, and a timeline shows the journey in
 *     order, with some steps updating themselves from RSVP, seating and
 *     invitation progress
 *   - Éros can read what is in the planning space, answer from it, and make a
 *     change when asked to
 *
 * And what it must never say, because the product does not do it: plan the
 * wedding, decide anything on its own, find, contact or book vendors, or
 * replace a planner. Éros suggests and the couple decides — stated in the copy,
 * not implied.
 *
 * ── Structure ─────────────────────────────────────────────────────────────
 * Section numbers are dropped, as in every guide since the third. Order carries
 * real meaning in three places, and each is a `steps` list that numbers itself:
 * the guest-list-to-seating sequence, and the two dependency chains. The
 * closing checklist uses `subheading` groups so it stays a summary of the
 * article rather than a second, longer article.
 *
 * No copy in this file may contain "&", "<" or ">". The generator escapes them
 * correctly, but `guides.seo.test.mjs` compares against tag-stripped HTML
 * without decoding entities, so a string containing one is reported missing
 * from a document that in fact carries it.
 */

const weddingPlanningChecklistEn = {
  slug: "wedding-planning-checklist",

  title: "Wedding Planning Checklist: What to Organize and When",
  metaTitle: "Wedding Planning Checklist: What to Organize and When | Agapaé",
  metaDescription:
    "A practical wedding planning checklist covering what to organize first, what to book next, and what to handle in the final weeks before your wedding.",

  eyebrow: "Wedding planning",

  hero: {
    src: "/assets/images/moments/moment_2.jpg",
    // Describes the photograph, not the keyword: a high-key black and white
    // frame, the couple walking forward hand in hand into bright light, both
    // cropped below the shoulders, the bride carrying a bouquet. It is the
    // moment everything in this article is planned toward, which is why it is
    // the hero. Nothing is described here that is not in the frame.
    alt: "A soft black and white photograph of a bride and groom walking forward hand in hand into bright light, the groom in a dark tuxedo with a bow tie and a flower at his lapel, the bride in a square-neck gown carrying a bouquet",
    width: 6558,
    height: 3659,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `A wedding arrives as one long list, and everything on it feels urgent at the same time. Very little of it actually is.`,
    `Most wedding tasks are waiting on a decision nobody has made yet. A caterer cannot quote you without a guest count. Tables cannot be drawn before the replies are in.`,
    `So the order that helps is not the order things occur to you. It is: budget and guest count, then date and venue, then the vendors who are hard to replace, then invitations and RSVPs, then the details, then seating and final numbers, then the logistics of the wedding week.`,
    `This wedding planning checklist follows that sequence rather than a calendar. The months below are approximate; the order is what does the work.`,
  ],

  sections: [
    {
      heading: "The Wedding Planning Checklist at a Glance",
      content: [
        `Most wedding planning timelines are built around twelve months. It is a useful frame rather than a rule: weddings are planned well in eighteen months and well in eight weeks.`,
        `Broadly, the work falls into seven windows:`,
        {
          type: "list",
          items: [
            `12 months or more: budget, guest count, date, location and venue`,
            `roughly 9 to 6 months: the core vendors, and the design they work to`,
            `roughly 6 to 3 months: invitations, attire and the guest-facing details`,
            `roughly 3 to 1 months: RSVPs, seating and the first real logistics`,
            `the final weeks: confirmations, payments, timeline and handoffs`,
            `the wedding week: execution rather than new decisions`,
            `after the wedding: closing out what is left`,
          ],
        },
        `Each window depends on the one before it, which is why the sequence survives a short engagement and a fixed calendar does not.`,
      ],
    },
    {
      heading: "12 Months or More Before: Set the Foundation",
      content: [
        `The first decisions constrain every decision after them, and are worth making deliberately.`,
        {
          type: "list",
          items: [
            `agree what you can spend, and who is contributing`,
            `estimate the guest count, and draft the guest list`,
            `decide your date, or a range of dates, and the general location`,
            `research venues and secure the one you want`,
            `decide whether you want a planner, or a coordinator for the day`,
          ],
        },
        `Budget, guest count and venue are one decision in three parts: the count you want may not fit the venue you love, and the venue you can afford depends on how many people are in the room. Expect to move between them until the three agree.`,
        `There is no correct budget, and no percentage split that survives a real wedding.`,
        {
          type: "p",
          text: [
            `The guest list is the input almost every later decision reads, so it is worth building as a working record rather than a page of names. `,
            {
              text: "How to Organize a Wedding Guest List",
              href: "/guides/how-to-organize-wedding-guest-list",
            },
            ` covers the layers it needs.`,
          ],
        },
      ],
    },
    {
      heading: "9 to 6 Months Before: Book the Vendors You Cannot Replace",
      content: [
        `Once the date and venue are fixed, vendors become bookable — most will not hold a date without both.`,
        `The bookings that usually come first:`,
        {
          type: "list",
          items: [
            `photographer and videographer`,
            `caterer, where the venue does not provide one`,
            `music and entertainment, whether a band, a DJ, or both`,
            `florist, decor, and the design direction they work from`,
            `the officiant, or whoever will lead your ceremony`,
            `a planner or coordinator, if you did not book one earlier`,
          ],
        },
        `The rule worth keeping is not the month. It is this: book the vendors that are both important to you and difficult to replace first.`,
        `Which ones those are is local. In peak season, sought-after venues and photographers are often booked a year or more ahead, while florists and cake makers can frequently be secured on shorter notice. Ask each vendor how far ahead they are booking, and let the answers set your order.`,
      ],
    },
    {
      heading: "6 to 3 Months Before: Build the Guest Experience",
      content: [
        `The middle stretch is where a wedding becomes something guests will experience, and where most visible decisions live.`,
        {
          type: "list",
          items: [
            `invitation design, and a wedding website or information page if you are using one`,
            `attire for you both and the wedding party, the fittings that follow, and rings`,
            `menu decisions, a tasting where one is offered, and the cake`,
            `flowers and decor in detail`,
            `transportation, and accommodation information for anyone travelling`,
            `ceremony details, readings, and music for each part of the day`,
            `the honeymoon, or whatever follows`,
          ],
        },
        `No wedding needs all of it. What matters is the character of the work: these decisions can be made comfortably, because nothing else is waiting on them. A few months later that stops being true.`,
      ],
    },
    {
      heading: "When Should You Send Wedding Invitations?",
      content: [
        `Invitation timing is a small decision with a long tail. When invitations go out decides how long guests have to answer, which sets your RSVP deadline, which decides when you can give anyone a final number.`,
        `How much lead time you need depends on things particular to your wedding:`,
        {
          type: "list",
          items: [
            `whether guests are travelling, and whether the wedding is abroad`,
            `whether you sent save-the-dates, which buys back some of the runway`,
            `production and delivery time for anything printed`,
            `when your caterer or venue needs numbers`,
          ],
        },
        {
          type: "p",
          text: [
            `Working those backwards from the wedding date is a subject of its own, and `,
            {
              text: "When Should Wedding Guests RSVP?",
              href: "/guides/when-should-wedding-guests-rsvp",
            },
            ` goes through the deadline and the response window in detail.`,
          ],
        },
      ],
    },
    {
      heading: "3 to 1 Months Before: Turn Plans Into Confirmed Numbers",
      content: [
        `This is the change of gear: from here, planning is names, numbers and times rather than decisions.`,
        {
          type: "list",
          items: [
            `track RSVPs against the list you sent, and review it for duplicates and late additions`,
            `follow up individually with guests who have not replied`,
            `confirm what your caterer needs, and by when`,
            `start the seating plan with the answers you have`,
            `finalize ceremony and reception details, transportation and venue access`,
            `finalize signage, menus and place cards once numbers settle`,
            `attend final fittings`,
          ],
        },
        {
          type: "p",
          text: [
            `Replies arrive in every format a person can send one, and keeping them straight is most of the work in this phase. `,
            {
              text: "How to Organize Wedding RSVPs Without Losing Track of Guests",
              href: "/guides/how-to-organize-wedding-rsvps",
            },
            ` covers the tracking side.`,
          ],
        },
        `Start the seating plan before every reply is in: a plan built from most of the answers absorbs the rest, while one started after the last reply is built under real time pressure.`,
      ],
    },
    {
      heading: "Why the Guest List Comes Before the Seating Plan",
      content: [
        `The most useful thing to understand about wedding planning is which decisions release which.`,
        {
          type: "steps",
          items: [
            `The guest list decides who is invited.`,
            `The invitations put that decision in front of people.`,
            `The RSVPs record what those people answered.`,
            `The confirmed attendees become your final headcount.`,
            `The headcount decides catering quantities and how many tables you need.`,
            `The seating plan arranges those attendees at those tables.`,
          ],
        },
        `Each step consumes the one before it. Tables drawn before the replies are in are drawn twice, and a headcount taken from a list nobody reconciled is a number you cannot defend.`,
        {
          type: "p",
          text: [
            `That last step is a piece of work in its own right, and `,
            {
              text: "How to Create a Wedding Seating Plan: From RSVP to Every Table",
              href: "/guides/wedding-seating-plan",
            },
            ` picks the chain up at the point your answers are in.`,
          ],
        },
      ],
    },
    {
      heading: "One Month Before: Stop Adding Decisions",
      content: [
        `In the last month, the most valuable thing you can do is stop having new ideas. Almost everything from here is confirmation rather than creation:`,
        {
          type: "list",
          items: [
            `confirm final guest numbers by the date each vendor needs them`,
            `confirm arrival and setup times with everyone working on the day`,
            `finalize the seating plan, and the signage, menus and place cards that come out of it`,
            `confirm transportation, and any accommodation arranged for guests`,
            `check what is still owed, and when each payment falls due`,
            `finalize the timeline for the day, and who is responsible for what`,
          ],
        },
        `A late addition is rarely just itself: a new idea in the final month usually arrives with a payment, a delivery, a change to the timeline and several people who have to be told.`,
        {
          type: "note",
          text: `The legal side of marrying — documents, notice periods, where they are filed — varies by country and region, and sometimes by the venue. Check what applies where you are marrying, and its deadlines, early enough that it is never a last-month problem.`,
        },
      ],
    },
    {
      heading: "The Final Week: Hand Off the Details",
      content: [
        `The final week is a handover, not a sprint. Whatever is still only in your head has to leave it: on the day, you should not be the one answering questions about the flowers.`,
        `A handover usually contains:`,
        {
          type: "list",
          items: [
            `the timeline for the day, and vendor contacts with arrival times`,
            `venue access, setup and clearing arrangements`,
            `what is still to be paid, to whom and how, under your contracts`,
            `everything that has to reach the venue, and who is carrying it`,
            `personal items, rings and documents, and who to call when something does not go to plan`,
          ],
        },
        `Name that person out loud — a coordinator, someone in the wedding party, a relative who likes a list — and tell your vendors who they are, so the questions reach someone who can answer them while you are getting dressed.`,
        `Where gratuities are customary, what is usual varies by country, vendor and contract. Check what your contracts say, ask locally if you are unsure, and prepare it in advance rather than on the morning.`,
      ],
    },
    {
      heading: "Wedding Day: Follow the Plan, Don't Build It",
      content: [
        `The wedding day is for execution. By then, someone should be holding:`,
        {
          type: "list",
          items: [
            `the final timeline, and the agreed responsibilities`,
            `vendor contacts, and someone to call about anything unexpected`,
            `personal items, and anything being transported`,
            `whatever documents the ceremony requires`,
          ],
        },
        `Something will go slightly differently than planned; it nearly always does, and almost none of it is visible to anyone else. The planning was the work. The day is the wedding.`,
      ],
    },
    {
      heading: "After the Wedding: Close the Loop",
      content: [
        `A few things outlive the day:`,
        {
          type: "list",
          items: [
            `settle whatever remains with your vendors, under your contracts`,
            `return hired items — attire, decor, equipment — within their agreed windows`,
            `collect anything left at the venue, and keep the ceremony documents safe`,
            `gather the photographs and video, from your photographer and from your guests`,
            `write to the people who travelled, helped, or gave something`,
            `keep vendor records, and put the things worth keeping somewhere they will last`,
          ],
        },
        `The deadlines here belong to your contracts and suppliers rather than to any checklist: return windows, final balances and how long a gallery stays available are set by whoever is providing them.`,
      ],
    },
    {
      heading: "What If You Have Less Than 12 Months?",
      content: [
        `Short engagements work. What does not work is keeping the twelve-month plan and deleting whichever tasks feel least important: those are usually the ones later decisions were waiting on.`,
        `Compress the sequence instead. The order stays; the windows shorten and overlap.`,
        { type: "subheading", text: "If you have around six months" },
        {
          type: "list",
          items: [
            `settle budget, guest count, date and venue in the first few weeks`,
            `approach the vendors who are hardest to replace immediately`,
            `stay flexible on the date and the day of the week, because availability is the real constraint`,
            `send save-the-dates or invitations as soon as the venue is confirmed`,
            `drop optional complexity, especially anything slow to produce`,
          ],
        },
        { type: "subheading", text: "If you have around three months" },
        {
          type: "list",
          items: [
            `ask who is available before deciding what you want`,
            `invite guests quickly, with a clear, early deadline to reply`,
            `keep to the core: a place, food, someone to photograph it, someone to lead the ceremony, music`,
            `keep the same dependency order, with days between the steps instead of months`,
          ],
        },
        `A longer timeline does not make a wedding better, and a shorter one does not make it worse. What it removes is room to revisit decisions, which is why the order matters more, not less.`,
      ],
    },
    {
      heading: "A Better Way to Think About the Checklist",
      content: [
        `A wedding checklist is not a hundred things to tick off. It is a small number of decisions, each releasing the ones behind it. Two chains carry most of a wedding.`,
        `The guest chain:`,
        {
          type: "steps",
          items: [
            `Your guest count sets the size of the wedding.`,
            `The venue has to hold that number, and catering is quoted against it.`,
            `Invitations are addressed from the list behind it.`,
            `RSVPs turn that list into confirmed attendees.`,
            `The final headcount is the number your vendors are given, and seating is drawn from it.`,
          ],
        },
        `The date chain:`,
        {
          type: "steps",
          items: [
            `Your date and venue decide which vendors are available, and availability decides who you can book.`,
            `The bookings decide the shape of the day, which becomes the timeline.`,
            `The timeline is what guest information and travel details are written from.`,
          ],
        },
        `Read that way, a checklist stops being a memory test. The question is not how much is left, but what is currently blocking something else — and that is almost always the thing to do next.`,
      ],
    },
    {
      // The brief closes with a separate Agapaé section, which is what the
      // `related` block below already renders as an H2. Two H2s reading the
      // same words is a defect, and every earlier guide resolved it the same
      // way: this section is named for what it does, and "Where this fits in
      // Agapaé" stays with the related block.
      heading: "Keeping the Whole Plan in One Place",
      content: [
        `Whatever you plan with — a notebook, a spreadsheet, a shared document — one rule matters more than the tool: there is one version of the plan, and it is the one that is right.`,
        {
          // The only plan-availability claim in the guide, in the same place
          // and the same block type every earlier guide put theirs.
          type: "note",
          text: `Agapaé's Planning Hub and Éros are part of the Heirloom plan.`,
        },
        `In Agapaé, planning is divided into the parts a wedding is made of — the venue, the food, the photography, the flowers — each holding its own vendors, tasks, costs, documents and open decisions. Anything dated appears on a calendar, and a timeline shows the journey in order, some of its steps keeping themselves current as your RSVPs arrive and your seating comes together.`,
        `Éros is the planning assistant inside that space: it reads what is already there, answers questions about your own wedding, and makes a change when you ask it to. It suggests and you decide — Agapaé does not choose your vendors, book anyone, or plan the wedding for you.`,
        {
          type: "p",
          text: [
            `The planning side is described on `,
            { text: "Wedding Planning", href: "/wedding-planning" },
            `, where the guest list, RSVPs and seating live on the same plan.`,
          ],
        },
      ],
    },
    {
      heading: "The Practical Wedding Planning Checklist",
      content: [
        `The whole article, compressed:`,
        { type: "subheading", text: "Foundation" },
        {
          type: "list",
          items: [
            `set a budget you both agree on`,
            `estimate the guest count and draft the list`,
            `choose a date and location, and secure the venue`,
          ],
        },
        { type: "subheading", text: "Major bookings" },
        {
          type: "list",
          items: [
            `book the vendors that are hardest to replace`,
            `confirm contracts and deposits in writing`,
            `settle the design direction`,
          ],
        },
        { type: "subheading", text: "Guest experience" },
        {
          type: "list",
          items: [
            `decide how you are inviting people, and when`,
            `prepare guest information, attire, ceremony and reception`,
          ],
        },
        { type: "subheading", text: "Guest management" },
        {
          type: "list",
          items: [
            `send the invitations`,
            `track RSVPs, and follow up on what has not arrived`,
            `confirm the final headcount`,
          ],
        },
        { type: "subheading", text: "Final planning" },
        {
          type: "list",
          items: [
            `finish the seating plan and the stationery from it`,
            `confirm every vendor, the timeline, and the logistics`,
          ],
        },
        { type: "subheading", text: "Wedding week" },
        {
          type: "list",
          items: [
            `hand off responsibilities to a named person`,
            `circulate contacts and the timeline`,
            `prepare personal items and documents`,
            `let the plan run`,
          ],
        },
        `If you can say what is currently blocking something else, you are further along than the length of any list suggests.`,
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the eight earlier guides: Google restricts FAQ rich results to
  // government and health sites, so the markup would buy nothing while
  // committing us to a second copy of every answer.
  faq: [
    {
      q: `When should I start planning my wedding?`,
      a: `As soon as you have a date in mind, or even just a season. Many couples find twelve to eighteen months comfortable, largely because venues and in-demand vendors are often booked that far ahead in busy markets. Shorter timelines work too. What changes is how quickly the early decisions have to be made, not the order they come in.`,
    },
    {
      q: `What should I do first when planning a wedding?`,
      a: `Budget and guest count, before anything is booked. Together they decide which venues are realistic, and the venue then decides your date and much of what follows. Draft the guest list alongside them: it is the number nearly every later quote is based on.`,
    },
    {
      q: `Can I plan a wedding in six months?`,
      a: `Yes, and many couples do. Compress the sequence rather than dropping tasks at random: settle budget, guest count, date and venue in the first few weeks, approach the vendors who are hardest to replace immediately, invite guests as soon as the venue is confirmed, and stay flexible about the date and the day of the week. On a short timeline, availability is the real constraint.`,
    },
    {
      q: `When should wedding invitations be sent?`,
      a: `Far enough ahead that guests can answer before you need a final count. That depends on whether they are travelling, whether you sent save-the-dates, how they will reply, and when your caterer needs numbers. Work backwards from the wedding: final headcount first, then the RSVP deadline, then the response window, and the send date falls out of it.`,
    },
    {
      q: `What should I do in the final month before my wedding?`,
      a: `Confirm rather than create. Give each vendor your final numbers by the date they need them, agree arrival and setup times, finish the seating plan and anything printed from it, build the timeline for the day, check what is still owed under each contract, and decide who is responsible for what.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // The two ends of the dependency chain this hub is built on. The RSVP
    // guides are already linked once each in the prose, at the points the
    // subject changes; naming all four here would be link volume rather than a
    // relationship, and no href in this guide is linked more than twice.
    guides: ["how-to-organize-wedding-guest-list", "wedding-seating-plan"],

    product: [
      {
        href: "/wedding-planning",
        label: "Wedding Planning",
        blurb:
          "See how the Planning Hub holds your vendors, tasks, budget, documents and decisions in one private space, with a timeline and a calendar over all of it. Available on the Heirloom plan.",
      },
    ],
  },
};

export default weddingPlanningChecklistEn;
